import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import cx from 'classnames'
import { StoreSnapshot, Editor, createTLStore } from '@tldraw/tldraw'
import Styles from './FullScreenPreviewWithCarouselV3.module.scss'

import { PostPreview } from '../PostPreview'
import { FullScreenPreviewProps, TYPES_WITH_PREVIEW } from '../Types'
import { Header } from './Header'
import { LoadingPreview } from '../LoadingPreview/LoadingPreview'
import { carouselClasses } from '../carouselClasses'
import { useIsScheduledPostsV3 } from 'hooks/isScheduledPostsV3'
import { Chat } from 'components/Chat'
import { useMarkFileAsViewed } from '../../useMarkFileAsViewed'
import { Sidebar } from './Sidebar'
import { PreviewAlertBanner } from './PreviewAlertBanner'
import { useRefreshIntegrationConnection } from 'hooks/useRefreshIntegrationConnection'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { MediaObject, ScheduledPostTarget } from 'types'
import { shapeUtils } from 'components/VisualStudioPlayground/VisualEditor/VisualEditor'
import { isBlankDesign as checkIsBlankDesign } from 'components/Design/utils/isBlankDesign'
import { DesignChannel } from 'components/NewFileModal/Types'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { useContentPreviewFiles } from 'hooks/calendar/useContentPreviewFiles'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { ViewAsMenu } from '../ViewAsMenu/ViewAsMenu'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { useFullscreenBackNavigation } from '../useFullscreenBackNavigation'
import { useOpenFullscreenPost } from '../../WeekViewV2/useOpenLastFullscreenPost'
import { useDesignPreviewBroadcast } from '../useDesignPreviewBroadcast'
import { ContentRating } from '../ContentRating/ContentRating'
import { ChatComparisonProvider, useIsComparing } from '../ChatComparisonContext'
import { CurrentSlideProvider } from '../CurrentSlideContext'
import { useShowNextGenContentPreferences } from 'hooks/useShowNextGenContentPreferences'
import { ChatComparison } from '../ChatComparison/ChatComparison'
import { useAuth } from 'hooks/UseAuth'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { CROSSPOST_UNSUPPORTED_CHANNELS } from '../FullScreenPreviewWithCarouselV2'
import { getCrossPostGroupForChannel } from '../utils'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { forwardGutterWheel } from './forwardGutterWheel'
import { DEFAULT_LOADING_COLOR } from 'screens/Calendar/WeekViewV2/utils'
import { CalendarEventCard } from 'screens/Calendar/WeekViewV2/CalendarEventCard/CalendarEventCard'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'
import { GenerationFailedPreview } from 'screens/Calendar/WeekViewV2/CalendarEventCard/GenerationFailedPreview/GenerationFailedPreview'

type Direction = 'left' | 'right'

const VISIBLE_SLIDE_STYLE = { zIndex: 1, opacity: 1, pointerEvents: 'auto' as const }
const SENTINEL_STYLE = { top: 20, left: 0, height: 1, width: '100%', position: 'absolute' as const }

export const FullScreenPreviewWithCarouselV3 = ({
  id,
  startDate,
  filters,
  onClose,
  channel,
  width,
  height,
  assetType,
  loadingStateColor,
  showCarousel = true,
  useChat = true,
}: FullScreenPreviewProps) => {
  const auth = useAuth()
  const { markFileAsViewed } = useMarkFileAsViewed()
  const calendarV2 = useFlipperFlag('calendar_v2')

  const {
    currentItem,
    displayedFile,
    handleRemoveCurrentFile,
    handleUpdateAlmanacFile,
    isFileFetching,
    isFileLoading,
    nextFile,
    nextKey,
    previousFile,
    previousKey,
    setCurrentItem,
  } = useContentPreviewFiles({
    id,
    startDate,
    filters,
    partialCalendar: calendarV2,
    showCarousel,
    onClose,
  })

  const fileKey = displayedFile?.key || currentItem.id || id
  const filePostAt = displayedFile?.post_at || currentItem.startDate || startDate || ''

  useOpenFullscreenPost({
    fileKey,
    filePostAt,
    fileChannel: displayedFile?.channel,
  })
  const { useBackButtonListener, handleClose, handleBack } = useFullscreenBackNavigation({
    onClose,
    currentItemDate: displayedFile?.post_at || currentItem.startDate,
  })

  useBackButtonListener()

  useDesignPreviewBroadcast({
    designKey: displayedFile?.key,
    enabled: true,
    isRegenerating:
      (displayedFile?.asset_type === 'Design' || displayedFile?.asset_type === 'Video') &&
      !!displayedFile?.image_url &&
      !!(displayedFile?.image_url_generating || displayedFile?.is_generating),
  })

  const [isLoading, setIsLoading] = useState(false)
  const [previewPlatform, setActivePlatformState] = useState<ExternalIntegrationEnum>()
  const [activeIntegrationId, setActiveIntegrationId] = useState<number>()
  const [isInsertingMediaObjects, setIsInsertingMediaObjects] = useState(false)
  const [seeMore, setSeeMore] = useState(false)

  const setActivePlatform = useCallback((platform: ExternalIntegrationEnum | undefined, integrationId?: number) => {
    setActivePlatformState(platform)
    setActiveIntegrationId(integrationId)
  }, [])

  const scheduledPostTargetsEnabled = useIsScheduledPostsV3()
  const { showNextGenContentPreferences } = useShowNextGenContentPreferences()

  useEffect(() => {
    markFileAsViewed(fileKey)
  }, [fileKey, markFileAsViewed])

  useEffect(() => {
    const fileChannel = displayedFile?.channel || channel
    if (fileChannel) {
      const crossPostGroup = getCrossPostGroupForChannel(fileChannel)
      setActivePlatform(crossPostGroup[0])
    } else {
      setActivePlatform(undefined)
    }
  }, [displayedFile?.key, displayedFile?.channel, channel, setActivePlatform])

  const previewRef = useRef<HTMLDivElement>(null)
  const topSentinelRef = useRef<HTMLDivElement>(null)

  const [previewSize, setPreviewSize] = useState({ width: 0, height: 0 })

  useLayoutEffect(() => {
    const node = previewRef.current
    if (!node) return

    const measure = () => {
      const rect = node.getBoundingClientRect()
      setPreviewSize({ width: rect.width, height: rect.height })
    }

    measure()

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => measure())
      observer.observe(node)
      return () => observer.disconnect()
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }

    return undefined
  }, [displayedFile?.key])

  const elementWidth = previewSize.width
  const elementHeight = previewSize.height

  const showPostPreview = displayedFile?.channel && TYPES_WITH_PREVIEW.includes(displayedFile.channel)

  const currentItemIndex = useMemo(() => (previousFile ? 1 : 0), [previousFile])

  const hasRatingOverlay =
    !!displayedFile?.completed_ai_suggestion_request?.id && !displayedFile.completed_ai_suggestion_request.rated

  const currentItemClasses = useMemo(() => {
    if (!displayedFile) return {}
    return carouselClasses(
      displayedFile,
      nextFile || displayedFile,
      previousFile || displayedFile,
      showCarousel,
      currentItemIndex,
      elementHeight,
      elementWidth,
      !!(scheduledPostTargetsEnabled && showPostPreview),
      true,
      hasRatingOverlay,
    ).getCarouselItemClassNames(currentItemIndex)
  }, [
    displayedFile,
    nextFile,
    previousFile,
    currentItemIndex,
    elementHeight,
    elementWidth,
    scheduledPostTargetsEnabled,
    showPostPreview,
    showCarousel,
    hasRatingOverlay,
  ])

  const startNavigation = useCallback(
    (direction: Direction) => {
      flushSync(() => {
        if (direction === 'right' && nextKey) {
          setCurrentItem({ id: nextKey, startDate: nextFile?.post_at ?? null })
        } else if (direction === 'left' && previousKey) {
          setCurrentItem({ id: previousKey, startDate: previousFile?.post_at ?? null })
        }
      })
    },
    [nextKey, nextFile?.post_at, previousKey, previousFile?.post_at, setCurrentItem],
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (
        target.closest('[role="dialog"]') ||
        target.closest('textarea') ||
        target.closest('input') ||
        target.closest('[contenteditable="true"]')
      ) {
        return
      }

      if (e.key === 'Escape') handleBack()
      else if (e.key === 'ArrowLeft') startNavigation('left')
      else if (e.key === 'ArrowRight') startNavigation('right')
    },
    [handleBack, startNavigation],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  const isDesignBlank = useMemo(() => {
    if (!displayedFile?.design_content) return false
    const store = createTLStore({ shapeUtils })
    store.loadSnapshot(displayedFile.design_content as StoreSnapshot<any>)
    const editor = new Editor({
      store,
      shapeUtils: shapeUtils as any,
      tools: [],
      getContainer: () => document.createElement('div'),
    })
    const isBlank = checkIsBlankDesign(editor)
    editor.dispose()
    return isBlank
  }, [displayedFile?.design_content])

  const toolbarPostWithMediaObjects = useMemo(() => {
    if (!displayedFile) return undefined
    if (displayedFile?.scheduled_post?.media_objects.length === 0 && displayedFile?.image_url) {
      return {
        ...displayedFile?.scheduled_post,
        media_objects: [
          {
            id: 1,
            source_url: displayedFile?.image_url,
            thumbnail_url: displayedFile?.image_url,
            mime_type: 'image/png',
            metadata: { width: displayedFile?.width, height: displayedFile?.height },
            design_key: displayedFile?.key,
            position: 0,
          } as MediaObject,
        ],
      } as ScheduledPostShow
    }
    return displayedFile?.scheduled_post
  }, [displayedFile])

  const refreshIntegrationConnection = useRefreshIntegrationConnection()

  const handleRefreshConnection = useCallback(
    (target: ScheduledPostTarget) =>
      refreshIntegrationConnection(target.provider, target.external_integration_id ?? undefined),
    [refreshIntegrationConnection],
  )

  const shouldShowFileLoadingState = !displayedFile && (isFileLoading || isFileFetching)

  const handleDisplayedFileDelete = useCallback(
    (skipNavigation?: boolean) => handleRemoveCurrentFile(displayedFile?.key, skipNavigation),
    [handleRemoveCurrentFile, displayedFile?.key],
  )

  const isCurrentChannelUnsupported = CROSSPOST_UNSUPPORTED_CHANNELS.includes(displayedFile?.channel as string)

  const handleGutterWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      const carouselItem = previewRef.current?.firstElementChild as HTMLElement | null
      forwardGutterWheel(e, carouselItem, displayedFile?.asset_type)
    },
    [displayedFile?.asset_type],
  )

  const renderSlide = useCallback(
    (file: AlmanacFileShowItem) => {
      const viewAsMenuEnabled = channel && !isCurrentChannelUnsupported

      return (
        <div key={file.key} className={Styles.slide} ref={previewRef} data-role="current" style={VISIBLE_SLIDE_STYLE}>
          <div
            className={cx(Styles.carouselItem, currentItemClasses, {
              [Styles.overflowing]: !!seeMore,
            })}
          >
            <div id="preview-sentinel" ref={topSentinelRef} style={SENTINEL_STYLE} />
            <ConditionalWrapper
              condition={viewAsMenuEnabled}
              wrapper={(children) => (
                <div className={Styles.previewContentWrapper}>
                  <div className={Styles.viewAsMenuAnchor}>
                    <ViewAsMenu
                      activePlatform={previewPlatform}
                      onSelectPlatform={(platform: ExternalIntegrationEnum) => {
                        setActivePlatform(platform)
                        setSeeMore(false)
                      }}
                      fileChannel={(displayedFile?.channel || channel) as ChannelType | DesignChannel | GenerationType}
                      redesign
                    />
                  </div>
                  {children}
                </div>
              )}
            >
              <PostPreview
                almanacFile={file}
                isLoading={shouldShowFileLoadingState}
                showVideoControls
                isActive={true}
                setIsLoading={setIsLoading}
                onClose={handleClose}
                onDelete={handleDisplayedFileDelete}
                filters={filters}
                fileChannel={channel}
                startDate={startDate || undefined}
                previewPlatform={previewPlatform}
                onSelectPlatform={setActivePlatform}
                isInsertingMedia={isInsertingMediaObjects}
                isTransitioning={false}
                showHoverActionOverlay
                onSeeMoreToggle={(expanded: boolean) => setSeeMore(expanded)}
                seeMore={seeMore}
                hasRatingOverlay={hasRatingOverlay}
              />
            </ConditionalWrapper>
          </div>
        </div>
      )
    },
    [
      handleDisplayedFileDelete,
      channel,
      isCurrentChannelUnsupported,
      currentItemClasses,
      seeMore,
      shouldShowFileLoadingState,
      setIsLoading,
      handleClose,
      filters,
      startDate,
      previewPlatform,
      setActivePlatform,
      isInsertingMediaObjects,
      displayedFile?.channel,
      hasRatingOverlay,
    ],
  )

  if (displayedFile?.did_generation_fail) {
    return (
      <div className={Styles.fullScreenPreview}>
        <Header
          channel={channel ?? undefined}
          almanacFile={displayedFile}
          onClose={handleBack}
          isLoading={false}
          setIsLoading={setIsLoading}
          onUpdateAlmanacFile={handleUpdateAlmanacFile}
          filters={filters}
        />
        <GenerationFailedPreview
          fileId={displayedFile.key}
          variant="fullScreenPreview"
          onClose={handleBack}
          channel={displayedFile.channel}
          contentType={displayedFile.content_type}
        />
      </div>
    )
  }

  if (shouldShowFileLoadingState || !displayedFile || displayedFile?.generation_state === 'generating') {
    const isArticle = [ChannelType.BlogPost, ChannelType.Newsletter].includes(channel as ChannelType)

    return (
      <div className={Styles.fullScreenPreview}>
        <Header
          channel={channel ?? undefined}
          almanacFile={displayedFile || undefined}
          onClose={handleBack}
          isLoading
          isGenerating
          setIsLoading={setIsLoading}
          showPostActions={false}
          onUpdateAlmanacFile={handleUpdateAlmanacFile}
          filters={filters}
        />
        <div className={Styles.loading} data-testid="loading-preview">
          {displayedFile && !isArticle ? (
            <CalendarEventCard
              cardWidth={350}
              isSelectView={false}
              event={
                {
                  ...displayedFile,
                  start: displayedFile?.post_at || '',
                  end: displayedFile?.post_at || '',
                } as AlmanacFileCalendarEvent
              }
              showHeader={false}
              isSelected={false}
              onSelect={undefined}
              onEventClick={undefined}
              loadingColor={DEFAULT_LOADING_COLOR}
              canSelect={false}
              cardBorder={undefined}
            />
          ) : (
            <LoadingPreview
              channel={channel}
              onClose={handleBack}
              width={width}
              height={height}
              assetType={assetType}
              color={DEFAULT_LOADING_COLOR}
            />
          )}
        </div>
      </div>
    )
  }

  const isPosted = displayedFile?.post_status === PostStatusPropertyValue.Posted
  const showChat = useChat && displayedFile?.asset_type === 'Design' && !!auth && !isPosted
  const showComparison = showNextGenContentPreferences && showChat

  return (
    <div
      className={Styles.fullScreenPreview}
      data-chat-open={showChat}
      data-nextgen-chat={showNextGenContentPreferences ? 'on' : undefined}
      data-article-preview={displayedFile?.asset_type === 'Article' ? 'true' : undefined}
    >
      <Header
        channel={channel ?? undefined}
        almanacFile={displayedFile || undefined}
        onClose={handleBack}
        isLoading={isLoading || shouldShowFileLoadingState}
        setIsLoading={setIsLoading}
        onDelete={handleDisplayedFileDelete}
        goToPreviousFile={previousFile ? () => startNavigation('left') : undefined}
        goToNextFile={nextFile ? () => startNavigation('right') : undefined}
        onUpdateAlmanacFile={handleUpdateAlmanacFile}
        filters={filters}
      />

      <ConditionalWrapper
        condition={showComparison}
        wrapper={(children) => <ChatComparisonProvider>{children}</ChatComparisonProvider>}
      >
        <div
          className={Styles.body}
          data-chat-open={showChat}
          data-nextgen-chat={showNextGenContentPreferences ? 'on' : undefined}
        >
          <div className={Styles.leftColumn}>
            <div
              className={cx(Styles.chatContainer, { [Styles.isVisible]: showChat })}
              data-chat-animation="idle"
              data-current-visible={showChat}
              data-show-chat={showChat}
              data-nextgen-chat={showNextGenContentPreferences ? 'on' : undefined}
            >
              {showChat && displayedFile?.key && (
                <Chat
                  isOpen
                  fileId={displayedFile.key}
                  context={{ design_id: displayedFile.key }}
                  isNextGen={showNextGenContentPreferences}
                  channel={displayedFile.channel as DesignChannel}
                />
              )}
            </div>
          </div>

          <CurrentSlideProvider activePlatform={previewPlatform}>
            <div
              className={cx(Styles.centerColumn, { [Styles.isUnauthedUser]: !auth })}
              data-testid="preview-center-column"
              onWheel={handleGutterWheel}
            >
              <PreviewAlertBanner almanacFile={displayedFile} onRefreshConnection={handleRefreshConnection} />
              <ComparisonOrChildren displayedFile={displayedFile}>
                <div
                  className={cx(Styles.carousel, {
                    [Styles.isArticlePreview]: displayedFile?.asset_type === 'Article',
                  })}
                  data-phase="idle"
                  data-direction="right"
                  role="region"
                  aria-roledescription="carousel"
                >
                  {renderSlide(displayedFile)}
                </div>

                {displayedFile?.completed_ai_suggestion_request?.id && (
                  <div className={Styles.contentRatingContainer}>
                    <ContentRating
                      subjectId={displayedFile.completed_ai_suggestion_request.id}
                      fileKey={displayedFile.key}
                      initialRated={displayedFile.completed_ai_suggestion_request.rated}
                      className={Styles.contentRating}
                    />
                  </div>
                )}
              </ComparisonOrChildren>
            </div>
          </CurrentSlideProvider>

          <RightColumn>
            {displayedFile && channel && !!auth && (
              <Sidebar
                scheduledPost={toolbarPostWithMediaObjects as ScheduledPostShow}
                almanacFile={displayedFile}
                previewPlatform={previewPlatform}
                activeIntegrationId={activeIntegrationId}
                onSelectPlatform={setActivePlatform}
                isBlankDesign={isDesignBlank}
                onLoadingChange={setIsInsertingMediaObjects}
                isLoading={isLoading}
                isInsertingMedia={isInsertingMediaObjects}
                setIsLoading={setIsLoading}
                onClose={handleClose}
                onDelete={handleDisplayedFileDelete}
                filters={filters}
                fileChannel={displayedFile?.channel || channel}
                startDate={startDate || undefined}
                onUpdateAlmanacFile={handleUpdateAlmanacFile}
                isCurrentChannelUnsupported={isCurrentChannelUnsupported}
              />
            )}
          </RightColumn>
        </div>
      </ConditionalWrapper>
    </div>
  )
}

const ComparisonOrChildren = ({
  children,
  displayedFile,
}: {
  children: React.ReactNode
  displayedFile: AlmanacFileShowItem
}) => {
  const isComparing = useIsComparing()
  if (isComparing) return <ChatComparison displayedFile={displayedFile} />
  return <>{children}</>
}

const RightColumn = ({ children }: { children: React.ReactNode }) => {
  const isComparing = useIsComparing()
  if (isComparing) return null
  return <div className={Styles.rightColumn}>{children}</div>
}
